// @ts-strict-ignore
import React from "react";

import kebabCase from "lodash/kebabCase";

import TextLink from "components/parts/NextLink/TextLink";
import Table from "components/parts/table";

import { CustomizableSpan } from "./style";
import type { TopLevelBlock } from "@contentful/rich-text-types";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import type { RichContent } from "./types";
import { DisclaimerLink } from "styles/disclaimer";
import GeminiPromoBanner from "components/pages/Cryptopedia/Homepage/GeminiPromoBanner";

export interface ComponentConfig {
  Link?: any;
  Header1?: any;
  Header2?: any;
  Header3?: any;
  Header4?: any;
  Header5?: any;
  Header6?: any;
  Paragraph?: any;
  EmbeddedComponent?: any;
  EmbeddedAsset?: any;
  EntryLink?: any;
  ListItem?: any;
  UnorderedList?: any;
  OrderedList?: any;
}

export interface ParseRichTextProps {
  source: RichContent[] | TopLevelBlock[];
  config?: ComponentConfig;
}

export const buildHeaderContent = (content: RichContent[]): string => {
  // need type guard since only the text node type has a value property
  const values = content
    .filter((item) => item.nodeType === "text")
    .map((item) => (item.nodeType === "text" ? item.value?.trim() : ""));
  return values.join("");
};

const parseEmbeddedInlineEntries = (data, index) => {
  if (data?.target?.fields?.table) {
    const tableData = data.target.fields.table.tableData;
    return <Table key={index} data={tableData} />;
  }
  if (data?.target?.fields?.superscript) {
    const superscript = data.target.fields.superscript;
    if (data.target.fields.footnote) {
      return (
        <DisclaimerLink to="disclaimer" smooth="true" offset={-100}>
          {superscript}
        </DisclaimerLink>
      );
    }
    return <sup key={index}>{superscript}</sup>;
  }
  return null;
};

export const COMPONENT_CONFIG_KEYS: (keyof ComponentConfig)[] = [
  "Link",
  "Header1",
  "Header2",
  "Header3",
  "Header4",
  "Header5",
  "Header6",
  "Paragraph",
  "EmbeddedComponent",
  "EmbeddedAsset",
  "EntryLink",
  "ListItem",
  "UnorderedList",
  "OrderedList"
];

const ParseRichText: React.FunctionComponent<ParseRichTextProps> = ({
  source,
  config = {}
}: ParseRichTextProps) => {
  const {
    Link = TextLink,
    Header1,
    Header2,
    Header3,
    Header4,
    Header5,
    Header6,
    Paragraph,
    EmbeddedComponent = null,
    EmbeddedAsset = null,
    EntryLink = null,
    ListItem = (props) => <li {...props} />,
    UnorderedList = (props) => <ul {...props} />,
    OrderedList = (props) => <ol {...props} />
  } = config;
  const parsedContent = source.map(
    (item: RichContent | TopLevelBlock, index: number) => {
      // shared fields
      const { nodeType, data } = item;
      const content = nodeType !== "text" ? item.content : null;
      const validatedContent =
        nodeType !== "text" ? buildHeaderContent(item.content) : "";
      switch (nodeType) {
        case BLOCKS.HEADING_1: {
          return (
            <Header1 key={index} id={`section-${kebabCase(validatedContent)}`}>
              <ParseRichText source={content} config={config} />
            </Header1>
          );
        }
        case BLOCKS.HEADING_2: {
          return (
            <Header2 key={index} id={`section-${kebabCase(validatedContent)}`}>
              <ParseRichText source={content} config={config} />
            </Header2>
          );
        }
        case BLOCKS.HEADING_3: {
          return (
            <Header3 key={index} id={`section-${kebabCase(validatedContent)}`}>
              <ParseRichText source={content} config={config} />
            </Header3>
          );
        }
        case BLOCKS.HEADING_4: {
          return (
            <Header4 key={index} id={`section-${kebabCase(validatedContent)}`}>
              <ParseRichText source={content} config={config} />
            </Header4>
          );
        }
        case BLOCKS.HEADING_5: {
          return (
            <Header5 key={index} id={`section-${kebabCase(validatedContent)}`}>
              <ParseRichText source={content} config={config} />
            </Header5>
          );
        }
        case BLOCKS.HEADING_6: {
          return (
            <Header6 key={index} id={`section-${kebabCase(validatedContent)}`}>
              <ParseRichText source={content} config={config} />
            </Header6>
          );
        }
        case BLOCKS.PARAGRAPH: {
          return (
            <Paragraph key={index}>
              <ParseRichText source={content} config={config} />
            </Paragraph>
          );
        }
        case BLOCKS.OL_LIST: {
          return (
            <OrderedList key={index}>
              {content.map((node: TopLevelBlock, index: number) => {
                return (
                  <ListItem key={index}>
                    <ParseRichText source={node.content} config={config} />
                  </ListItem>
                );
              })}
            </OrderedList>
          );
        }
        case BLOCKS.UL_LIST: {
          return (
            <UnorderedList key={index}>
              {content.map((node: TopLevelBlock, index: number) => {
                return (
                  <ListItem key={index}>
                    <ParseRichText source={node.content} config={config} />
                  </ListItem>
                );
              })}
            </UnorderedList>
          );
        }
        case BLOCKS.EMBEDDED_ENTRY: {
          return <EmbeddedComponent fields={(data.target as any).fields} />;
        }
        case "text": {
          const { marks } = item;
          // https://www.smartercontent.info/post/how-to-add-line-breaks-to-any-text-field-in-contentful#:~:text=By%20default%2C%20Contentful%20will%20replace,a%20tag.
          const textWithLineBreaks = item.value
            .split("\n")
            .reduce((children, textSegment, index) => {
              return [
                ...children,
                index > 0 && <br key={index} />,
                textSegment
              ];
            }, []);

          // the below is a hack to allow for custom react components to be inserted into the rich text
          const customNodePrefix = "CUSTOM__REACT__NODE__";
          if (item.value.startsWith(customNodePrefix)) {
            switch (item.value) {
              case `${customNodePrefix}GEMINI_PROMO_BANNER`:
                return <GeminiPromoBanner cryptopedia key={index} />;
              default:
                return null;
            }
          } else if (marks.length === 0) {
            return <span key={index}>{textWithLineBreaks}</span>;
          } else {
            let bold = false;
            let italic = false;
            let underline = false;
            // allow multiple marks
            marks.forEach((mark) => {
              if (mark.type === "bold") {
                bold = true;
              } else if (mark.type === "italic") {
                italic = true;
              } else if (mark.type === "underline") {
                underline = true;
              }
            });
            if (marks.find((mark) => mark.type === "superscript")) {
              return <sup>{textWithLineBreaks}</sup>;
            }
            return (
              <CustomizableSpan
                bold={bold}
                italic={italic}
                underline={underline}
                key={index}
              >
                {textWithLineBreaks}
              </CustomizableSpan>
            );
          }
        }
        case INLINES.HYPERLINK: {
          return (
            <Link key={index} href={data.uri}>
              {content.map((node) =>
                node.nodeType === "text" ? node.value : null
              )}
            </Link>
          );
        }
        case INLINES.ENTRY_HYPERLINK: {
          return (
            EntryLink && (
              <EntryLink key={index} target={data.target}>
                {content.map((node) =>
                  node.nodeType === "text" ? node.value : null
                )}
              </EntryLink>
            )
          );
        }
        case INLINES.EMBEDDED_ENTRY: {
          return parseEmbeddedInlineEntries(data, index);
        }
        case BLOCKS.EMBEDDED_ASSET: {
          const assetFile = (data.target as any)?.fields?.file;
          const id = data.target.sys?.id;
          return (
            EmbeddedAsset && <EmbeddedAsset key={id} id={id} file={assetFile} />
          );
        }
        default:
          return (
            <Paragraph key={index}>
              <ParseRichText source={content} config={config} />
            </Paragraph>
          );
      }
    }
  );
  return <React.Fragment>{parsedContent}</React.Fragment>;
};

export default ParseRichText;
